import { InjectionToken } from '@angular/core';
import { PageHeaderBreadcrumbItem } from '@shared/components/page-header/page-header.component';
import { AssistantType } from './conversation.model';

/** Suggestion de démarrage cliquable affichée sur une conversation vierge. */
export interface AssistantChatSuggestion {
  icon: string;
  label: string;
  text: string;
}

/** Catégorie d'exemples affichée dans le message d'accueil du composant (sans conversation). */
export interface AssistantChatExampleCategory {
  icon: string;
  title: string;
  items: string[];
}

/**
 * Configuration d'une page de chat assistant (Ingénieur, Juridique RH…).
 *
 * La page, le composant de chat, le service de chat et la barre d'historique sont
 * communs : seule cette configuration change d'un assistant à l'autre. Elle est
 * fournie par la route via `provideAssistantChat(...)` (services/provide-assistant-chat.ts) ; sans fourniture explicite,
 * c'est la configuration de l'Assistant Ingénieurs qui s'applique.
 */
export interface AssistantChatConfig {
  /** Type des conversations enregistrées (filtre de l'historique). */
  assistantType: AssistantType;
  /** Chemin de l'API de chat, relatif à environment.apiUrl (ex. 'api/assistant-chat'). */
  apiPath: string;
  /** Clé localStorage de l'identifiant de réponse précédente (chaînage OpenAI). */
  threadStorageKey: string;
  /** Clé localStorage de la conversation courante. */
  conversationStorageKey: string;
  /** Route de la page (rechargée après suppression d'une conversation). */
  route: string;
  title: string;
  subtitle: string;
  /** Icône Lucide (kebab-case), enregistrée dans main.ts. */
  icon: string;
  breadcrumb: PageHeaderBreadcrumbItem[];
  /** Bandeau permanent affiché sous le titre (avertissement), optionnel. */
  disclaimer?: string;
  /** Premier message (Markdown) affiché par l'assistant sur une conversation vierge. */
  welcomeMessage: string;
  /** Phrase d'introduction du bloc d'accueil (affiché quand aucun message n'est chargé). */
  introText: string;
  exampleCategories: AssistantChatExampleCategory[];
  suggestions: AssistantChatSuggestion[];
  /**
   * Export Word des réponses : préfixe du nom de fichier
   * (`<prefixe>-AAAA-MM-JJ.docx`). Absent : pas de bouton d'export.
   */
  wordExportFilePrefix?: string;
  /**
   * Dépôt de fichiers avec les messages (agent Plans de fabrication). Absent : pas de bouton
   * trombone. Les fichiers sont déposés dans la conversation avant l'envoi du message.
   */
  piecesJointes?: AssistantChatPiecesJointes;
  /** Faux : le titre de la conversation n'est pas tiré du premier message (le serveur le fixe). */
  titreAutomatique?: boolean;
}

/** Dépôt de fichiers dans le chat. */
export interface AssistantChatPiecesJointes {
  /** Types proposés par le sélecteur (« image/*,.pdf » : l'iPhone convertit le HEIC en JPEG). */
  accept: string;
  nbMax: number;
  tailleMaxMo: number;
  /** Chemin de dépôt (POST multipart « fichier »), relatif à environment.apiUrl. */
  cheminDepot: (conversationId: string) => string;
  /** Texte envoyé quand l'utilisateur dépose des fichiers sans rien écrire. */
  messageParDefaut: string;
}

/** Configuration historique de l'Assistant Ingénieurs (page /assistant-chat). */
export const ENGINEER_ASSISTANT_CONFIG: AssistantChatConfig = {
  assistantType: AssistantType.Engineer,
  apiPath: 'api/assistant-chat',
  threadStorageKey: 'assistant_chat_thread',
  conversationStorageKey: 'assistant_chat_conversation',
  route: '/assistant-chat',
  title: 'Assistant Ingénieurs',
  subtitle: 'Documentation technique et fiches produits',
  icon: 'hard-hat',
  breadcrumb: [
    { label: 'Tableau de bord', icon: 'home', route: '/dashboard' },
    { label: 'Assistant Ingénieurs', icon: 'hard-hat' }
  ],
  welcomeMessage: `👋 **Bienvenue sur l'Assistant Ingénieurs !**

Je suis votre assistant spécialisé pour vous aider sur :

📚 **Documentation technique**
- Normes et réglementations (DTU, NF, etc.)
- Fiches techniques produits
- Guides de mise en œuvre
- Procédures techniques

 **Comment puis-je vous aider aujourd'hui ?**

Posez-moi vos questions techniques, je suis là pour vous guider !`,
  introText: 'Je suis votre assistant intelligent pour les ingénieurs.',
  exampleCategories: [
    {
      icon: 'book-open',
      title: 'Documentation technique',
      items: ['Quelles normes DTU pour une installation CVC ?', 'Comment dimensionner une PAC air/eau ?']
    },
    {
      icon: 'wrench',
      title: 'Mise en œuvre',
      items: ["Procédure d'installation d'un système de ventilation ?", "Guide de mise en service d'une chaudière ?"]
    },
    {
      icon: 'settings',
      title: 'Normes et réglementations',
      items: ['Quelle réglementation thermique RT 2020 ?', 'Normes de sécurité pour les installations gaz ?']
    }
  ],
  suggestions: [
    { icon: 'book-open', label: 'Normes & DTU', text: 'Quelles normes DTU s\'appliquent à une installation de plomberie sanitaire ?' },
    { icon: 'settings', label: 'Dimensionnement', text: 'Comment dimensionner une pompe à chaleur air/eau pour une maison de 120 m² ?' },
    { icon: 'wrench', label: 'Mise en œuvre', text: 'Quelle est la procédure de mise en service d\'une chaudière gaz à condensation ?' },
    { icon: 'file-text', label: 'Réglementation', text: 'Quelles sont les règles de sécurité à respecter pour une installation gaz ?' },
  ]
};

/** Configuration de l'Assistant Juridique RH (page /juridique-rh). */
export const JURIDIQUE_RH_ASSISTANT_CONFIG: AssistantChatConfig = {
  assistantType: AssistantType.JuridiqueRH,
  apiPath: 'api/juridique-rh-chat',
  threadStorageKey: 'juridique_rh_chat_thread',
  conversationStorageKey: 'juridique_rh_chat_conversation',
  route: '/juridique-rh',
  title: 'Assistant Juridique RH',
  subtitle: 'Droit du travail, conventions collectives SYNTEC, Métallurgie et Bâtiment, procédures internes',
  icon: 'scale',
  breadcrumb: [
    { label: 'Tableau de bord', icon: 'home', route: '/dashboard' },
    { label: 'Assistant Juridique RH', icon: 'scale' }
  ],
  // Texte identique à la ligne de limite du CDC (section 6) reprise par le prompt serveur (LIGNE_LIMITE).
  disclaimer: "Aide à la recherche, ne constitue pas un avis juridique. Faire valider toute décision individuelle par la direction et, si besoin, par le cabinet ou l'avocat.",
  welcomeMessage: `**Bienvenue sur l'Assistant Juridique RH**

Je vous aide à rechercher et à rédiger sur :

- le **Code du travail** et les conventions collectives du groupe (**SYNTEC**, **Métallurgie**, **Bâtiment**), avec l'article cité et sa source ;
- les **comparatifs** entre la loi et les conventions, et la convention applicable à chaque société ;
- les **dernières publications du Journal officiel** sur le travail et l'emploi, avec le lien Légifrance ;
- les **procédures internes** du groupe (notes de frais, congés, déplacements, formulaires) ;
- la préparation de **documents RH** à partir des modèles du groupe, exportables en Word.

Décrivez le cas **sans nom de salarié** et précisez si besoin la société, la catégorie (ouvrier, ETAM, cadre) et l'ancienneté.`,
  introText: 'Je suis votre assistant de recherche en droit du travail et procédures RH.',
  exampleCategories: [
    {
      icon: 'scale',
      title: 'Droit du travail',
      items: ["Durée maximale d'une période d'essai pour un cadre ?", "Étapes d'un licenciement pour inaptitude ?", 'Quoi de neuf au Journal officiel cette semaine ?']
    },
    {
      icon: 'book-open',
      title: 'Conventions collectives',
      items: ['Prime de vacances SYNTEC ?', 'Préavis d\'un ETAM qui démissionne chez AES ?']
    },
    {
      icon: 'clipboard-list',
      title: 'Procédures internes',
      items: ['Note de frais pour un déplacement de deux jours ?', 'Démarche pour un congé pour mariage ?']
    }
  ],
  suggestions: [
    { icon: 'hourglass', label: "Période d'essai", text: "Quelle est la durée maximale d'une période d'essai pour un cadre ?" },
    { icon: 'calendar-days', label: 'Prime de vacances', text: 'Quelle prime de vacances prévoit la convention SYNTEC ?' },
    { icon: 'scale', label: 'Comparatif', text: "Compare l'indemnité de licenciement entre le Code du travail, SYNTEC, Métallurgie et BTP." },
    { icon: 'megaphone', label: 'Journal officiel', text: 'Quoi de neuf au Journal officiel cette semaine pour les RH ?' },
    { icon: 'users', label: 'Application au groupe', text: 'Quel préavis pour un ETAM qui démissionne chez AES ?' },
    { icon: 'clipboard-list', label: 'Procédure interne', text: 'Comment je fais une note de frais pour un déplacement de deux jours ?' },
    { icon: 'file-text', label: 'Document RH', text: 'Prépare un avenant de passage à temps partiel à 80 %.' },
  ],
  wordExportFilePrefix: 'assistant-juridique-rh'
};

/** Configuration de l'Assistant SSI (page /assistant-ssi). */
export const SSI_ASSISTANT_CONFIG: AssistantChatConfig = {
  assistantType: AssistantType.Ssi,
  apiPath: 'api/ssi-chat',
  threadStorageKey: 'ssi_chat_thread',
  conversationStorageKey: 'ssi_chat_conversation',
  route: '/assistant-ssi',
  title: 'Assistant SSI',
  subtitle: 'Sécurité incendie : règlements ERP et IGH, normes SSI',
  icon: 'flame',
  breadcrumb: [
    { label: 'Tableau de bord', icon: 'home', route: '/dashboard' },
    { label: 'Assistant SSI', icon: 'flame' }
  ],
  // Texte identique à la ligne de limite du prompt serveur (SsiChatController.LIGNE_LIMITE).
  disclaimer: "Aide à la recherche réglementaire et normative : ne remplace ni l'avis du bureau de contrôle, ni celui du coordonnateur SSI, ni la décision de la commission de sécurité.",
  welcomeMessage: `**Bienvenue sur l'Assistant SSI**

Je vous aide sur les systèmes de sécurité incendie :

- le **règlement de sécurité ERP** (arrêté du 25 juin 1980 : dispositions générales, articles MS, dispositions particulières par type) et le **règlement IGH**, importés de Légifrance, avec l'article cité, sa date de version et son lien ;
- les **normes SSI** (NF S61-931 à 942, NF S61-970, NF EN 54, NF EN 12101) : laquelle s'applique à quel matériel ;
- l'**installation**, la **mise en service**, la **maintenance** et les **vérifications** d'un SSI.

Précisez si possible le **type d'établissement** (ERP de type U, J, M…, ou IGH) et sa **catégorie**.`,
  introText: 'Je suis votre assistant pour la réglementation et les normes de sécurité incendie.',
  exampleCategories: [
    {
      icon: 'building-2',
      title: 'Réglementation ERP / IGH',
      items: ['Quelle catégorie de SSI pour un ERP de type U de 2e catégorie ?', "Quel équipement d'alarme dans un type J ?"]
    },
    {
      icon: 'siren',
      title: 'Matériels et normes',
      items: ['Quelles normes pour un clapet coupe-feu utilisé comme DAS ?', 'Quelle norme pour un CMSI ?']
    },
    {
      icon: 'wrench',
      title: 'Maintenance et vérifications',
      items: ['Qui vérifie un SSI de catégorie A et à quelle périodicité ?', "Règles d'exploitation et de maintenance d'un SSI ?"]
    }
  ],
  suggestions: [
    { icon: 'building-2', label: 'Catégorie de SSI', text: "Quelle catégorie de SSI faut-il pour un ERP de type U de 2e catégorie avec locaux à sommeil ?" },
    { icon: 'bell-ring', label: "Équipement d'alarme", text: "Quel type d'équipement d'alarme est exigé dans un ERP de type M de 1re catégorie ?" },
    { icon: 'siren', label: 'Normes DAS', text: "Quelles normes doit respecter un volet de désenfumage piloté par un CMSI ?" },
    { icon: 'cloud-fog', label: 'Désenfumage', text: 'Quelles sont les règles générales de désenfumage des circulations horizontales dans un ERP ?' },
    { icon: 'clipboard-check', label: 'Vérifications', text: "Quelles vérifications techniques pour un SSI de catégorie A, et par qui ?" },
    { icon: 'calendar-clock', label: 'Évolutions', text: 'Quelles évolutions réglementaires SSI en 2025 et 2026 ?' },
  ],
  wordExportFilePrefix: 'assistant-ssi'
};

/** Agent Plans de fabrication (page /plans-fabrication) : croquis → plan PDF A4 indicé. */
export const PLANS_FABRICATION_CONFIG: AssistantChatConfig = {
  assistantType: AssistantType.PlansFabrication,
  apiPath: 'api/plans-fabrication-chat',
  threadStorageKey: 'plans_fabrication_thread',
  conversationStorageKey: 'plans_fabrication_conversation',
  route: '/plans-fabrication',
  title: 'Plans de fabrication',
  subtitle: "Du croquis à main levée au plan d'atelier",
  icon: 'drafting-compass',
  breadcrumb: [
    { label: 'Tableau de bord', icon: 'home', route: '/dashboard' },
    { label: 'Plans de fabrication', icon: 'drafting-compass' }
  ],
  welcomeMessage: `**Bienvenue sur l'agent Plans de fabrication**

Déposez le **croquis à main levée** d'une pièce de tôlerie (photo ou PDF) avec le trombone : dévoiement, gaine, caisson, plénum, piquage.

1. Je vous dis ce que je lis et je pose les questions qui manquent (bords droits, sens du décalage, épaisseur, pression, matière…) ;
2. je produis le **plan A4** (vues, iso, cotes, cartouche) : provisoire tant qu'il manque des données ;
3. vos corrections donnent l'**indice suivant** (A → B → C) ; le plan s'envoie par mail après votre validation.`,
  introText: 'Je transforme vos croquis de tôlerie aéraulique en plans de fabrication.',
  exampleCategories: [
    { icon: 'drafting-compass', title: 'Croquis', items: ['Déposez une photo ou un scan du croquis', 'Précisez la pièce si besoin : « dévoiement de la gaine R+2 »'] },
    { icon: 'pencil', title: 'Corrections', items: ['« Il y a un bord droit de 50 de chaque côté »', '« Décalage 200 au lieu de 160 »'] },
    { icon: 'mail', title: 'Envoi', items: ["« Envoie le plan au chargé d'affaires »"] }
  ],
  suggestions: [],
  piecesJointes: {
    accept: 'image/*,application/pdf,.pdf',
    nbMax: 4,
    tailleMaxMo: 30,
    cheminDepot: id => `api/plans-fabrication/conversations/${encodeURIComponent(id)}/fichiers`,
    messageParDefaut: 'Voici le croquis.'
  },
  titreAutomatique: false
};

/**
 * Toutes les configurations de chat : sert notamment à effacer à la déconnexion les identifiants
 * de conversation conservés dans le localStorage (poste partagé).
 */
export const ALL_ASSISTANT_CHAT_CONFIGS: readonly AssistantChatConfig[] = [
  ENGINEER_ASSISTANT_CONFIG,
  JURIDIQUE_RH_ASSISTANT_CONFIG,
  SSI_ASSISTANT_CONFIG,
  PLANS_FABRICATION_CONFIG
];

/** Configuration courante de la page de chat (Ingénieur par défaut). */
export const ASSISTANT_CHAT_CONFIG = new InjectionToken<AssistantChatConfig>('ASSISTANT_CHAT_CONFIG', {
  providedIn: 'root',
  factory: () => ENGINEER_ASSISTANT_CONFIG
});

